import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "components-lib";
import { FiRefreshCcw } from "react-icons/fi";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { SocketProvider } from "context/SocketProvider";
import AddClinicModal from "views/admin/doctorsList/AddClinicModal";
import AddFrontdeskModal from "views/admin/doctorsList/AddFrontdeskModal";
import { createFrontdeskFormDefaults } from "views/admin/shared/formDefaults";
import { useClinicList } from "./hooks/useClinicList";
import { clinicColumnsData } from "views/admin/default/variables/columnsData";
import DoctorForm from "../doctorsList/DoctorForm";
import { axiosInstance } from "config/api";
import { getAuthAccounts } from "../../../api/googleAuthAPI";
import ClinicEditModal from "./ClinicEditModal";
import GoogleCalendarWidget from "views/admin/settings/components/GoogleCalendarWidget";

export default function ClinicList() {
  const toast = useToast();
  const {
    clinics,
    setClinics,
    isLoading,
    refreshClinics,
  } = useClinicList();

  const [googleAccounts, setGoogleAccounts] = useState([]);

  // Fetch Google accounts
  const refreshGmeetStatus = useCallback(async () => {
    try {
      const res = await getAuthAccounts();
      const accounts = res?.data?.accounts || [];
      setGoogleAccounts(accounts);
    } catch (error) {
      console.error("Failed to fetch Google accounts:", error);
      toast({
        title: "Status Check Failed",
        description: "Could not verify Google Meet connection status.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  }, []);

  // Initial fetch of Google accounts
  useEffect(() => {
    refreshGmeetStatus();
  }, [refreshGmeetStatus]);

  // Google auth helper (used to open auth popup for a specific clinic email)
  // const { } = useGoogleAuth({
  //   onSuccess: () => {
  //     // refresh clinic list after successful auth
  //     refreshClinics();
  //   },
  //   onError: (err) => {
  //     toast({
  //       title: "Authentication error",
  //       description: err?.message || "Google authentication failed",
  //       status: "error",
  //       duration: 5000,
  //       isClosable: true,
  //       position: "top",
  //     });
  //   },
  // });
  const {
    isOpen: isAddClinicOpen,
    onOpen: onOpenAddClinicModal,
    onClose: onCloseAddClinicModal,
  } = useDisclosure();
  const {
    isOpen: isAddFrontdeskOpen,
    onOpen: onOpenAddFrontdeskModal,
    onClose: onCloseAddFrontdeskModalInternal,
  } = useDisclosure();

  const {
    isOpen: isEditClinicOpen,
    onOpen: onOpenEditClinicModal,
    onClose: onCloseEditClinicModalInternal,
  } = useDisclosure();

  const {
    isOpen: isDoctorFormOpen,
    onOpen: onDoctorFormOpen,
    onClose: onDoctorFormClose,
  } = useDisclosure();

  const [frontdeskForm, setFrontdeskForm] = useState(() =>
    createFrontdeskFormDefaults(),
  );
  const [selectedClinic, setSelectedClinic] = useState(null);

  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedClinicId, setSelectedClinicId] = useState(null);

  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  const handleFrontdeskModalClose = useCallback(() => {
    setFrontdeskForm(createFrontdeskFormDefaults());
    onCloseAddFrontdeskModalInternal();
  }, [onCloseAddFrontdeskModalInternal]);

  const handleFrontdeskCreated = useCallback(() => {
    setFrontdeskForm(createFrontdeskFormDefaults());
    refreshClinics();
  }, [refreshClinics]);

  const handleClinicCreated = useCallback(() => {
    refreshClinics();
  }, [refreshClinics]);

  const handleAddDoctor = useCallback(() => {
    refreshClinics();
  }, [refreshClinics]);

  const handleEditClinic = useCallback(
    (clinic) => {
      setSelectedClinic(clinic);
      onOpenEditClinicModal();
    },
    [onOpenEditClinicModal],
  );

  const handleCloseEditClinicModal = useCallback(() => {
    setSelectedClinic(null);
    onCloseEditClinicModalInternal();
  }, [onCloseEditClinicModalInternal]);

  // deleteClinic deprecated; keep locking actions in toggleClinicLock

  // New: toggle lock/unlock on clinic
  const toggleClinicLock = useCallback(
    async (clinicId, lock) => {
      // helper to build urls without double slashes
      const buildUrl = (base, endpoint, id) => {
        const parts = [base, endpoint, String(id)]
          .map((p) => (p ? p.replace(/(^\/+|\/+$)/g, "") : ""))
          .filter(Boolean);
        // ensure leading slash so axios appends to baseURL correctly
        return "/" + parts.join("/");
      };

      const endpointName = lock ? "lock-clinic" : "unlock-clinic";

      // candidate base paths — extend if your backend uses other prefixes
      const bases = ["/api"];

      // try both common methods (sample used PUT; older APIs may accept POST)
      const methods = ["put", "post"];

      let lastError = null;
      for (const base of bases) {
        for (const method of methods) {
          const url = buildUrl(base, endpointName, clinicId);
          try {
            const response = await axiosInstance.request({
              method,
              url,
              data: {}, // payload if required by API can be added here
              validateStatus: (status) => status < 500, // allow 4xx to be handled
            });

            if (response.status >= 200 && response.status < 300) {
              return true;
            }

            // if 404/405 try next candidate
            if ([404, 405].includes(response.status)) {
              lastError = new Error(`Not found/method not allowed (${method.toUpperCase()} ${url})`);
              continue;
            }

            lastError = new Error(`Unexpected status ${response.status} for ${method.toUpperCase()} ${url}`);
          } catch (error) {
            lastError = error;
            const status = error?.response?.status;
            if (status && [404, 405].includes(status)) {
              // try next candidate
              continue;
            }
            // for other errors stop trying this URL/method and bubble up after trying others
            continue;
          }
        }
      }

      throw lastError || new Error("Failed to toggle clinic lock");
    },
    [],
  );

  const handleUn_LockClinic = useCallback(
    async (clinic) => {
      // This handler now toggles lock/unlock instead of deleting
      if (!clinic?.id) return;

      const isLocked = clinic.isLocked ?? clinic.locked ?? false;
      const action = isLocked ? "unlock" : "lock";

      const confirmed = window.confirm(
        `${action === "lock" ? "Lock" : "Unlock"} clinic "${clinic.clinicName || "this clinic"
        }"? This action ${action === "lock" ? "can be reverted by unlocking." : "will allow access again."}`,
      );
      if (!confirmed) return;

      try {
        await toggleClinicLock(clinic.id, action === "lock");
        toast({
          title: `Clinic ${action === "lock" ? "locked" : "unlocked"}`,
          description: `Clinic ${action === "lock" ? "locked" : "unlocked"} successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        refreshClinics();
      } catch (error) {
        console.error(`Error ${action}ing clinic:`, error);
        toast({
          title: `${action[0].toUpperCase() + action.slice(1)} failed`,
          description:
            error.response?.data?.message ||
            `Could not ${action} clinic. Please try again.`,
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [toggleClinicLock, refreshClinics],
  );

  // Explicit unlock action (used by the UI when an explicit unlock button is provided)
  const handleUnlockClinic = useCallback(
    async (clinic) => {
      if (!clinic?.id) return;

      const confirmed = window.confirm(
        `Unlock clinic "${clinic.clinicName || "this clinic"}"? This will restore access.`,
      );
      if (!confirmed) return;

      try {
        await toggleClinicLock(clinic.id, false);
        toast({
          title: `Clinic unlocked`,
          description: `Clinic unlocked successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        refreshClinics();
      } catch (error) {
        console.error("Error unlocking clinic:", error);
        toast({
          title: `Unlock failed`,
          description:
            error.response?.data?.message || `Could not unlock clinic. Please try again.`,
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [toggleClinicLock, refreshClinics],
  );

  // Append a GMeet status column to the clinic columns for per-clinic checks
  const checkGmeetStatus = useCallback(
    async (clinic) => {
      if (!clinic) return;

      // If clinic does not have an email saved, fetch clinic details first
      let clinicEmail = clinic.clinicEmail;
      if (!clinicEmail) {
        try {
          // Prefer clinic/allClinicsWithFullData backend for clinic details if available
          let detailsRes;
          try {
            detailsRes = await axiosInstance.get("api/clinic/allClinicsWithFullData", { params: { id: clinic.id } });
            if (!detailsRes || detailsRes.status === 404 || !detailsRes.data) {
              throw new Error('No clinic details returned');
            }
          } catch (e) {
            // fallback to legacy endpoint
            detailsRes = await axiosInstance.get("api/clinic/clinicById", { params: { id: clinic.id } });
          }
          const d = detailsRes?.data || {};
          // try multiple shapes for clinicEmail returned by clinic/allClinicsWithFullData or older endpoints
          clinicEmail = d?.clinic?.clinic_email || d?.clinic_email || d?.data?.clinic_email || d?.data?.clinicEmail || d?.clinicEmail || d?.email || null;
          if (clinicEmail) {
            // persist locally so subsequent checks don't re-fetch
            setClinics((prev) =>
              prev.map((c) => (c.id === clinic.id ? { ...c, clinicEmail } : c)),
            );
            toast({
              title: "Clinic email fetched",
              description: `Using ${clinicEmail} for Google authentication`,
              status: "info",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          } else {
            toast({
              title: "No clinic email",
              description:
                "Clinic details do not contain an email to use for Google authentication.",
              status: "warning",
              duration: 4000,
              isClosable: true,
              position: "top",
            });
            return;
          }
        } catch (error) {
          console.error("Failed to fetch clinic details:", error);
          toast({
            title: "Error",
            description:
              error?.response?.data?.message || "Could not fetch clinic details.",
            status: "error",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
          return;
        }
      }

      // If we have a clinicEmail, check if it's in the authenticated accounts
      try {
        const isConnected = googleAccounts.some((account) =>
          String(account?.account_email || "").toLowerCase() === String(clinicEmail).toLowerCase()
        );

        // No need to update local clinics state manually as it is derived from googleAccounts
        // setClinics((prev) =>
        //   prev.map((c) => (c.id === clinic.id ? { ...c, gmeetStatus: status } : c)),
        // );

        toast({
          title: isConnected ? "GMeet Connected" : "GMeet Not Connected",
          description: isConnected
            ? `${clinicEmail} is authenticated for Google Calendar.`
            : `${clinicEmail} is not authenticated for Google Calendar.`,
          status: isConnected ? "success" : "info",
          duration: 3500,
          isClosable: true,
          position: "top",
        });

        // Open the Google Calendar widget modal
        setSelectedEmail(clinicEmail);
        setSelectedClinicId(clinic.id || null);
        setIsGoogleModalOpen(true);
      } catch (error) {
        console.error("Error checking Google accounts:", error);
        toast({
          title: "Failed to check GMeet status",
          description: "Could not verify Google Calendar authentication status.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [setClinics, googleAccounts],
  );

  const columnsData = useMemo(() => {
    const extra = {
      Header: (
        <Flex align="center" gap={2}>
          GMeet
          <IconButton
            aria-label="Refresh GMeet status"
            icon={<FiRefreshCcw />}
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              refreshGmeetStatus();
            }}
          />
        </Flex>
      ),
      accessor: "gmeet",
      disableSortBy: true,
      Cell: ({ row }) => {
        const clinic = row.original;
        const status = clinic?.gmeetStatus || "not_connected";
        const label = status === "connected" ? "Connected" : "Not Connected";
        return (
          <Flex gap={2} align="center">
            <Button
              size="sm"
              colorScheme={status === "connected" ? "green" : "gray"}
              onClick={() => checkGmeetStatus(clinic)}
            >
              {label}
            </Button>
          </Flex>
        );
      },
    };

    return [...clinicColumnsData, extra];
  }, [checkGmeetStatus, refreshGmeetStatus]);

  const tableData = useMemo(
    () =>
      clinics.map((clinic) => {
        // Normalize lock flag from various possible shapes returned by API
        const rawLocked =
          clinic.is_locked ?? clinic.isLocked ?? clinic.locked ?? false;
        const isLocked = Boolean(rawLocked);

        // Provide both snake_case and camelCase for downstream consumers
        const normalizedClinic = {
          ...clinic,
          is_locked: rawLocked,
          isLocked,
        };

        // Derive gmeetStatus from googleAccounts
        const clinicEmail = normalizedClinic.clinicEmail || normalizedClinic.clinic_email;
        // Priority: backend-provided gmeetStatus if present, else derive from googleAccounts
        let gmeetStatus = normalizedClinic.gmeetStatus || "not_connected";
        if ((!gmeetStatus || gmeetStatus === 'not_connected') && clinicEmail) {
          const isConnected = googleAccounts.some((account) =>
            String(account?.account_email || "").toLowerCase() === String(clinicEmail).toLowerCase()
          );
          if (isConnected) gmeetStatus = "connected";
        }

        return {
          ...normalizedClinic,
          gmeetStatus, // Add derived status
          onEdit: () => handleEditClinic(normalizedClinic),
          // onDelete retains toggle behavior (will lock when unlocked, unlock when locked)
          onDelete: () => handleUn_LockClinic(normalizedClinic),
          // explicit unlock action
          onUnlock: () => handleUnlockClinic(normalizedClinic),
        };
      }),
    [clinics, handleUn_LockClinic, handleEditClinic, handleUnlockClinic, googleAccounts],
  );


  return (
    <SocketProvider>
      <Box
        pt={{ base: "130px", md: "80px", xl: "80px" }}
        w="100%"
        overflowX="auto"
        bg={boxBg}
        p={4}
        mr={0}
        mb={4}
        borderRadius="md"
        classname=" mr-0"
      >
        <Flex justify="flex-end" align="center" mb={4} gap={4}>
          <Flex gap={4}>
            <Button colorScheme="orange" onClick={onOpenAddClinicModal}>
              Add Clinic
            </Button>
            <Button colorScheme="teal" onClick={onDoctorFormOpen}>
              Add Doctor
            </Button>
            <Button colorScheme="purple" onClick={onOpenAddFrontdeskModal}>
              Add Frontdesk
            </Button>
          </Flex>
          {/* <IconButton
            aria-label="Refresh clinic list"
            icon={<FiRefreshCcw />}
            onClick={() => {
              refreshClinics();
              refreshGmeetStatus();
            }}
            isRound
            isDisabled={isLoading}
          /> */}
        </Flex>
        <ComplexTable
          columnsData={columnsData}
          tableData={tableData}
          setTableDataComplex={setClinics}
          isLoading={isLoading}
        />
        <AddClinicModal
          isOpen={isAddClinicOpen}
          onClose={onCloseAddClinicModal}
          onClinicCreated={handleClinicCreated}
        />
        <AddFrontdeskModal
          isOpen={isAddFrontdeskOpen}
          onClose={handleFrontdeskModalClose}
          frontdeskForm={frontdeskForm}
          setFrontdeskForm={setFrontdeskForm}
          frontdeskLoading={false}
          onFrontdeskCreated={handleFrontdeskCreated}
        />
        <DoctorForm
          isOpen={isDoctorFormOpen}
          onClose={onDoctorFormClose}
          handleAddDoctor={handleAddDoctor}
        />
        <ClinicEditModal
          isOpen={isEditClinicOpen}
          onClose={handleCloseEditClinicModal}
          clinic={selectedClinic}
          onClinicUpdated={refreshClinics}
        />
        <GoogleCalendarWidget
          isOpen={isGoogleModalOpen}
          onClose={() => {
            setIsGoogleModalOpen(false);
            setSelectedEmail(null);
            setSelectedClinicId(null);
            refreshGmeetStatus();
          }}
          email={selectedEmail}
          clinicId={selectedClinicId}
        />
      </Box>
    </SocketProvider>
  );
}
